import React, { useState, useEffect } from 'react';
import * as api from '../services/api';
import '../styles/asset-points-modal.css';

/**
 * Modal to display detailed point information for a specific RWA asset
 */
const AssetPointsModal = ({ isOpen, onClose, assetData, walletData }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [pointDetails, setPointDetails] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'transactions', 'projections'

  useEffect(() => {
    if (isOpen && assetData?.order_id) {
      fetchAssetPointsDetails();
    }
  }, [isOpen, assetData]);

  // Function to fetch detailed point information for this specific asset/order
  const fetchAssetPointsDetails = async () => {
    if (!assetData?.order_id || !walletData?.address) {
      setError("Missing required data to fetch point details");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Get signature message
      const messageResponse = await fetch('https://api.aeronyx.network/api/generate-signature-message/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_address: walletData.address,
          blockchain_network_id: walletData.network === 'solana' ? 3 : (walletData.network === 'bsc' ? 2 : 1)
        }),
      });
      
      if (!messageResponse.ok) {
        throw new Error('Failed to generate signature message');
      }
      
      const messageData = await messageResponse.json();
      
      if (!messageData.success || !messageData.data || !messageData.data.message) {
        throw new Error('Invalid signature message response');
      }
      
      // Sign message with the wallet
      const signature = await api.signMessage(
        walletData.type,
        walletData.address,
        messageData.data.message
      );
      
      if (!signature) {
        throw new Error('Failed to sign message with wallet');
      }

      // Call the order points details endpoint
      const response = await fetch('https://api.aeronyx.network/api/points/order-details/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_address: walletData.address,
          order_id: assetData.order_id,
          blockchain_network_id: walletData.network === 'solana' ? 3 : (walletData.network === 'bsc' ? 2 : 1),
          signature: signature,
          message: messageData.data.message,
          wallet_type: walletData.type
        }),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch point details');
      }

      setPointDetails(result.data);
    } catch (error) {
      console.error('Error fetching asset point details:', error);
      setError(`Failed to load point details: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Format currency for display
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '$0.00';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format number with commas
  const formatNumber = (value, decimals = 2) => {
    if (value === undefined || value === null) return '0';
    
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: decimals
    }).format(value);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Render point stats summary in the overview tab
  const renderPointStatsSummary = () => {
    if (!pointDetails?.point_stats) return null;
    
    const stats = pointDetails.point_stats;
    
    return (
      <div className="point-stats-grid">
        <div className="point-stat-card">
          <div className="stat-label">Total Points Generated</div>
          <div className="stat-value">{formatNumber(stats.total_points_generated)}</div>
        </div>
        
        <div className="point-stat-card">
          <div className="stat-label">Net Points Received</div>
          <div className="stat-value highlighted">{formatNumber(stats.net_points_received || (stats.total_points_generated - stats.total_commissions_paid - stats.total_system_pool))}</div>
        </div>
        
        <div className="point-stat-card">
          <div className="stat-label">Daily Generation (Net)</div>
          <div className="stat-value">{formatNumber(stats.daily_rate_net)}</div>
        </div>
        
        <div className="point-stat-card">
          <div className="stat-label">Hourly Generation (Net)</div>
          <div className="stat-value">{formatNumber(stats.hourly_rate_net)}</div>
        </div>
        
        <div className="point-stat-card">
          <div className="stat-label">Days Active</div>
          <div className="stat-value">{stats.days_active || 0}</div>
        </div>
        
        <div className="point-stat-card">
          <div className="stat-label">Total Commissions Paid</div>
          <div className="stat-value">{formatNumber(stats.total_commissions_paid)}</div>
        </div>
      </div>
    );
  };

  // Render package information
  const renderPackageInfo = () => {
    if (!pointDetails?.package_info) return null;
    
    const pkg = pointDetails.package_info;
    
    return (
      <div className="package-info">
        <div className="package-header">
          <h4>Package Information</h4>
        </div>
        <div className="package-details">
          <div className="package-detail">
            <span className="detail-label">Type:</span>
            <span className="detail-value">{pkg.type || assetData.type}</span>
          </div>
          <div className="package-detail">
            <span className="detail-label">Price:</span>
            <span className="detail-value">{formatCurrency(pkg.price_usdt)}</span>
          </div>
          <div className="package-detail">
            <span className="detail-label">Annual Yield:</span>
            <span className="detail-value">{pkg.annual_yield_percentage || 0}%</span>
          </div>
          {pkg.mining_factor && (
            <div className="package-detail">
              <span className="detail-label">Mining Factor:</span>
              <span className="detail-value">{pkg.mining_factor}x</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render transactions table
  const renderTransactions = () => {
    if (!pointDetails?.transactions || !Array.isArray(pointDetails.transactions) || pointDetails.transactions.length === 0) {
      return (
        <div className="empty-transactions">
          <p>No transactions found for this asset.</p>
        </div>
      );
    }
    
    return (
      <div className="transactions-container">
        <div className="transactions-table">
          <div className="table-header">
            <div className="header-cell">Date</div>
            <div className="header-cell">Type</div>
            <div className="header-cell">Amount</div>
            <div className="header-cell">Net Amount</div>
            <div className="header-cell">Status</div>
          </div>
          <div className="table-body">
            {pointDetails.transactions.map((tx, index) => (
              <div className="table-row" key={tx.transaction_id || index}>
                <div className="table-cell">{formatDate(tx.created_at)}</div>
                <div className="table-cell">
                  <span className={`tx-type tx-type-${tx.transaction_type}`}>
                    {tx.transaction_type.charAt(0).toUpperCase() + tx.transaction_type.slice(1)}
                  </span>
                </div>
                <div className="table-cell">{formatNumber(tx.amount)}</div>
                <div className="table-cell">
                  {tx.transaction_type === 'generation' ? formatNumber(tx.net_amount) : formatNumber(tx.amount)}
                </div>
                <div className="table-cell">
                  <span className={`tx-status tx-status-${tx.status}`}>
                    {tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render projections information
  const renderProjections = () => {
    if (!pointDetails?.point_stats) return null;
    
    const stats = pointDetails.point_stats;
    
    return (
      <div className="projections-container">
        <div className="projections-header">
          <h4>Earnings Projections</h4>
        </div>
        
        <div className="projections-grid">
          <div className="projection-card">
            <div className="projection-label">Projected Future Earnings (Gross)</div>
            <div className="projection-value">{formatNumber(stats.projected_earnings_gross)}</div>
          </div>
          
          <div className="projection-card">
            <div className="projection-label">Projected Future Earnings (Net)</div>
            <div className="projection-value highlighted">{formatNumber(stats.projected_earnings_net)}</div>
          </div>
          
          <div className="projection-card">
            <div className="projection-label">Projected Total Lifetime (Gross)</div>
            <div className="projection-value">{formatNumber(stats.projected_total_gross)}</div>
          </div>
          
          <div className="projection-card">
            <div className="projection-label">Projected Total Lifetime (Net)</div>
            <div className="projection-value highlighted">{formatNumber(stats.projected_total_net)}</div>
          </div>
          
          <div className="projection-card">
            <div className="projection-label">Days Remaining</div>
            <div className="projection-value">{stats.days_remaining || 'N/A'}</div>
          </div>
          
          <div className="projection-card">
            <div className="projection-label">Commission Rate</div>
            <div className="projection-value">{stats.commission_percentage || 0}%</div>
          </div>
        </div>
        
        <div className="projection-note">
          <p>Note: Projections are estimates based on current performance and may vary based on network conditions.</p>
        </div>
      </div>
    );
  };

  // Render daily totals chart
  const renderDailyTotals = () => {
    if (!pointDetails?.daily_totals || !Array.isArray(pointDetails.daily_totals) || pointDetails.daily_totals.length === 0) {
      return null;
    }
    
    // Sort daily totals by date (newest first)
    const sortedDailyTotals = [...pointDetails.daily_totals].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    ).slice(0, 7); // Show only last 7 days
    
    return (
      <div className="daily-totals">
        <div className="daily-totals-header">
          <h4>Recent Daily Earnings</h4>
        </div>
        
        <div className="daily-totals-table">
          <div className="table-header">
            <div className="header-cell">Date</div>
            <div className="header-cell">Gross Points</div>
            <div className="header-cell">Net Points</div>
          </div>
          <div className="table-body">
            {sortedDailyTotals.map((day, index) => (
              <div className="table-row" key={index}>
                <div className="table-cell">
                  {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
                <div className="table-cell">{formatNumber(day.gross_points)}</div>
                <div className="table-cell">{formatNumber(day.net_points)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // If modal is not open, return null
  if (!isOpen) return null;

  return (
    <div className="points-modal-overlay">
      <div className="points-modal-content">
        <div className="points-modal-header">
          <h3>{assetData?.name || 'Asset'} Points Details</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        {isLoading ? (
          <div className="modal-loading">
            <div className="spinner"></div>
            <p>Loading point details...</p>
          </div>
        ) : error ? (
          <div className="modal-error">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
              <path d="M12 8V12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
              <path d="M12 16H12.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <p>{error}</p>
            <button className="btn" onClick={fetchAssetPointsDetails}>Try Again</button>
          </div>
        ) : (
          <div className="points-modal-body">
            {/* Tab navigation */}
            <div className="points-tabs">
              <button 
                className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
              <button 
                className={`tab-btn ${activeTab === 'transactions' ? 'active' : ''}`}
                onClick={() => setActiveTab('transactions')}
              >
                Transactions
              </button>
              <button 
                className={`tab-btn ${activeTab === 'projections' ? 'active' : ''}`}
                onClick={() => setActiveTab('projections')}
              >
                Projections
              </button>
            </div>
            
            {/* Tab content */}
            <div className="tab-content">
              {activeTab === 'overview' && (
                <div className="overview-tab">
                  {renderPackageInfo()}
                  {renderPointStatsSummary()}
                  {renderDailyTotals()}
                </div>
              )}
              
              {activeTab === 'transactions' && (
                <div className="transactions-tab">
                  {renderTransactions()}
                </div>
              )}
              
              {activeTab === 'projections' && (
                <div className="projections-tab">
                  {renderProjections()}
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="points-modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default AssetPointsModal;
